import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
// __EQUIP_COMPAT__ (2026-10-04) the pair's capacity is DERIVED from the tractor + trailer
import { unitCapacity, typeInfo, fmtFt, fmtLb, EQUIPMENT_CLASSES } from '../utils/equipmentCompat';
import { kgToForm, formToKg, missingSpecs, unitLabels } from '../utils/equipmentForm';
import { getDefaultUnitSystem } from '../utils/units';
import { TRUCK_COLS, TRAILER_COLS, invalidateEquipmentCache } from '../services/equipmentService';
import { runAssignOps } from '../services/equipmentAssignService';   // __EQUIP_ASSIGN__

// What the pair can carry, and why (shown on the form and the list).
function ComboCapacity({ truck, trailer, capKg, units }) {
  if (!truck) return null;
  const cap = unitCapacity({ truck, trailer: trailer || null, combo: { combined_max_weight_kg: capKg } });
  if (!cap) return null;
  const miss = [...missingSpecs(truck, 'truck').map((m) => 'truck ' + m), ...(trailer ? missingSpecs(trailer, 'trailer').map((m) => 'trailer ' + m) : [])];
  return (
    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
      <div><strong>{EQUIPMENT_CLASSES[cap.cls]}</strong> ({cap.typeLabel})</div>
      {cap.tractorOnly && <div style={{ color: 'var(--color-danger)' }}>A tractor with no trailer cannot carry cargo (only trailer moves).</div>}
      <div>Payload: <strong>{cap.payloadLb != null ? fmtLb(cap.payloadLb, units) : 'unknown'}</strong>{cap.payloadFrom ? ' (' + cap.payloadFrom + ')' : ''}</div>
      {cap.cls !== 'bulk' && !cap.tractorOnly && <div>Deck: {fmtFt(cap.deckLengthFt, units)} x {fmtFt(cap.deckWidthFt, units)}{cap.deckHeightFt != null ? ', deck height ' + fmtFt(cap.deckHeightFt, units) : ''}{cap.insideHeightFt != null ? ', inside height ' + fmtFt(cap.insideHeightFt, units) : ''}</div>}
      {cap.features.length > 0 && <div>Features: {cap.features.join(', ')}</div>}
      {cap.out.map((o, i) => <div key={i} style={{ color: 'var(--color-danger)' }}>{o.what} is {o.why}</div>)}
      {miss.length > 0 && <div style={{ color: 'var(--color-warning)' }}>Missing for the equipment check: {miss.join(', ')}</div>}
    </div>
  );
}

function FleetCombinationsModal({ onClose, onSuccess }) {
  const [combinations, setCombinations] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCombination, setEditingCombination] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const units = getDefaultUnitSystem();

  const [formData, setFormData] = useState({
    combination_name: '',
    truck_id: '',
    trailer_id: '',
    default_driver_id: '',
    is_default: false,
    combined_max_weight_kg: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [combinationsRes, trucksRes, trailersRes, driversRes] = await Promise.all([
        supabase
          .from('truck_trailer_combinations')
          .select(`
            *,
            truck:trucks(${TRUCK_COLS}),
            trailer:trailers(${TRAILER_COLS}),
            default_driver:drivers(id, name, email)
          `)
          .order('combination_name', { ascending: true }),
        supabase
          .from('trucks')
          .select(TRUCK_COLS)
          .eq('is_active', true)
          .order('truck_number', { ascending: true }),
        supabase
          .from('trailers')
          .select(TRAILER_COLS)
          .eq('is_active', true)
          .order('trailer_number', { ascending: true }),
        supabase
          .from('drivers')
          .select('id, name, email, status')
          .order('name', { ascending: true })
      ]);

      if (combinationsRes.error) throw combinationsRes.error;
      if (trucksRes.error) throw trucksRes.error;
      if (trailersRes.error) throw trailersRes.error;
      if (driversRes.error) throw driversRes.error;

      setCombinations(combinationsRes.data || []);
      setTrucks(trucksRes.data || []);
      setTrailers(trailersRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      combination_name: '',
      truck_id: '',
      trailer_id: '',
      default_driver_id: '',
      is_default: false,
      combined_max_weight_kg: '',
      notes: ''
    });
    setEditingCombination(null);
    setError(null);
  };

  const handleEdit = (combination) => {
    setFormData({
      combination_name: combination.combination_name || '',
      truck_id: combination.truck_id || '',
      trailer_id: combination.trailer_id || '',
      default_driver_id: combination.default_driver_id || '',
      is_default: combination.is_default || false,
      combined_max_weight_kg: kgToForm(combination.combined_max_weight_kg, units),   // display units
      notes: combination.notes || ''
    });
    setEditingCombination(combination);
    setShowForm(true);
    setError(null);
  };

  const handleTruckChange = (truckId) => {
    const truck = trucks.find(t => t.id === truckId);
    const trailer = trailers.find(t => t.id === formData.trailer_id);
    
    // __EQUIP_COMPAT__ no longer auto-filled with min(truck GVWR, trailer GVWR) - that is not
    // what a pair can carry. The payload is derived (see ComboCapacity); this field is only
    // an optional manual cap.

    let name = formData.combination_name;
    if (!editingCombination || !formData.combination_name) {
      const truckNum = truck?.truck_number || '';
      const trailerNum = trailer?.trailer_number || '';
      name = trailerNum ? `${truckNum} + ${trailerNum}` : truckNum;
    }

    setFormData({
      ...formData,
      truck_id: truckId,
      combination_name: name
    });
  };

  const handleTrailerChange = (trailerId) => {
    const truck = trucks.find(t => t.id === formData.truck_id);
    const trailer = trailers.find(t => t.id === trailerId);
    
    // __EQUIP_COMPAT__ no longer auto-filled with min(truck GVWR, trailer GVWR) - that is not
    // what a pair can carry. The payload is derived (see ComboCapacity); this field is only
    // an optional manual cap.

    let name = formData.combination_name;
    if (!editingCombination || !formData.combination_name) {
      const truckNum = truck?.truck_number || '';
      const trailerNum = trailer?.trailer_number || '';
      name = trailerNum ? `${truckNum} + ${trailerNum}` : truckNum;
    }

    setFormData({
      ...formData,
      trailer_id: trailerId,
      combination_name: name
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!formData.combination_name.trim()) {
        throw new Error('Combination name is required');
      }
      if (!formData.truck_id) {
        throw new Error('Please select a truck');
      }

      const combinationData = {
        combination_name: formData.combination_name,
        truck_id: formData.truck_id,
        trailer_id: formData.trailer_id || null,
        is_default: formData.is_default,
        combined_max_weight_kg: formToKg(formData.combined_max_weight_kg, units),
        notes: formData.notes || null
      };

      if (formData.is_default) {
        await supabase
          .from('truck_trailer_combinations')
          .update({ is_default: false })
          .eq('truck_id', formData.truck_id);
      }

      if (editingCombination) {
        const { error } = await supabase
          .from('truck_trailer_combinations')
          .update(combinationData)
          .eq('id', editingCombination.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('truck_trailer_combinations')
          .insert([combinationData]);

        if (error) throw error;
      }

      // __EQUIP_ASSIGN__ the primary driver goes through equipment_assign() (audited; keeps the
      // many-to-many assignments, drivers.current_truck_id and default_driver_id in step)
      const prevDriver = editingCombination ? editingCombination.default_driver_id || '' : '';
      const nextDriver = formData.default_driver_id || '';
      if (nextDriver !== prevDriver || (nextDriver && editingCombination && editingCombination.truck_id !== formData.truck_id)) {
        const ops = [];
        if (prevDriver && editingCombination && editingCombination.truck_id) ops.push({ op: 'unassign', driver_id: prevDriver, truck_id: editingCombination.truck_id });
        if (nextDriver) ops.push({ op: 'assign', driver_id: nextDriver, truck_id: formData.truck_id, primary: true });
        await runAssignOps(ops);
      }
      invalidateEquipmentCache();
      await fetchData();
      resetForm();
      setShowForm(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving combination:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (combination) => {
    if (deleteConfirmId !== combination.id) { setDeleteConfirmId(combination.id); return; }
    setDeleteConfirmId(null);

    try {
      const { error } = await supabase
        .from('truck_trailer_combinations')
        .delete()
        .eq('id', combination.id);

      if (error) throw error;
      await fetchData();
    } catch (err) {
      console.error('Error deleting combination:', err);
      setError(err.message);
    }
  };

  const getTruckTypeLabel = (type) => typeInfo('truck', type).label;

  const getTrailerTypeLabel = (type) => typeInfo('trailer', type).label;

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '5px',
    color: 'var(--text-primary)',
    fontSize: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '0.9rem'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '2rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>
            {showForm ? (editingCombination ? 'Edit Combination' : 'Create Combination') : 'Truck-Trailer Combinations'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--color-danger)',
            color: 'var(--text-primary)',
            padding: '1rem',
            borderRadius: '5px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {showForm ? (
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Combination Name *</label>
                <input
                  type="text"
                  value={formData.combination_name}
                  onChange={(e) => setFormData({ ...formData, combination_name: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., T-001 + TR-001"
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>Truck *</label>
                <select
                  value={formData.truck_id}
                  onChange={(e) => handleTruckChange(e.target.value)}
                  style={inputStyle}
                  required
                >
                  <option value="">Select a truck...</option>
                  {trucks.map(truck => (
                    <option key={truck.id} value={truck.id}>
                      {truck.truck_number} ({getTruckTypeLabel(truck.truck_type)}){truck.status && truck.status !== 'available' && truck.status !== 'in_use' ? ' - ' + truck.status.replace(/_/g, ' ').toUpperCase() : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Trailer (optional)</label>
                <select
                  value={formData.trailer_id}
                  onChange={(e) => handleTrailerChange(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">No trailer (truck only)</option>
                  {trailers.map(trailer => (
                    <option key={trailer.id} value={trailer.id}>
                      {trailer.trailer_number} ({getTrailerTypeLabel(trailer.trailer_type)}){trailer.status && trailer.status !== 'available' && trailer.status !== 'in_use' ? ' - ' + trailer.status.replace(/_/g, ' ').toUpperCase() : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Primary driver (optional)</label>
                <select
                  value={formData.default_driver_id}
                  onChange={(e) => setFormData({ ...formData, default_driver_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">No default driver</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name || driver.email || `Driver ${driver.id.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Makes this truck his primary setup. Drivers who share it: the Equipment Assignment view.
                </small>
              </div>

              <div>
                <label style={labelStyle}>Manual payload cap ({unitLabels(units).weight}) - optional</label>
                <input
                  type="number"
                  value={formData.combined_max_weight_kg}
                  onChange={(e) => setFormData({ ...formData, combined_max_weight_kg: e.target.value })}
                  style={inputStyle}
                  placeholder="Leave blank to use the derived payload"
                  step="any"
                  min="0"
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Only if the pair may carry LESS than derived (e.g. axle or permit limit). The smallest limit wins.
                </small>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                <label htmlFor="is_default" style={{ color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Set as default for this truck
                </label>
              </div>

              {/* __EQUIP_COMPAT__ derived capacity of the pair */}
              <div style={{ gridColumn: '1 / -1', backgroundColor: 'var(--surface-2)', padding: '0.75rem', borderRadius: '5px' }}>
                <div style={{ color: 'var(--accent-primary)', fontWeight: 'bold', marginBottom: '0.3rem' }}>What this pair can carry</div>
                {formData.truck_id ? (
                  <ComboCapacity truck={trucks.find((t) => t.id === formData.truck_id) || (editingCombination && editingCombination.truck)}
                    trailer={formData.trailer_id ? (trailers.find((t) => t.id === formData.trailer_id) || (editingCombination && editingCombination.trailer)) : null}
                    capKg={formToKg(formData.combined_max_weight_kg, units)} units={units} />
                ) : <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Pick a truck.</div>}
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  Deck = the trailer's (else the truck's). Payload = the smallest of: the trailer's payload (or its GVWR - tare), the tractor's GCWR - both tares, and the manual cap.
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Any additional notes about this combination..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--surface-3)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: saving ? 'var(--surface-3)' : 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'Saving...' : (editingCombination ? 'Update Combination' : 'Create Combination')}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                + Create Combination
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading combinations...
              </div>
            ) : combinations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <p>No combinations yet.</p>
                <p style={{ fontSize: '0.9rem' }}>
                  Create combinations to pair trucks with trailers for easy load assignment.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '0.75rem',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {combinations.map((combo) => (
                  <div
                    key={combo.id}
                    style={{
                      backgroundColor: 'var(--surface-2)',
                      padding: '1rem',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: combo.is_default ? '2px solid var(--accent-primary)' : 'none'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {combo.combination_name}
                        </span>
                        {combo.is_default && (
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            borderRadius: '3px',
                            fontSize: '0.75rem',
                            backgroundColor: 'var(--accent-primary)',
                            color: 'var(--text-primary)'
                          }}>
                            Default
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--accent-blue)' }}>
                          {combo.truck?.truck_number || 'Unknown Truck'}
                        </span>
                        {combo.truck?.truck_type && (
                          <span style={{ color: 'var(--text-muted)' }}> ({getTruckTypeLabel(combo.truck.truck_type)})</span>
                        )}
                        {combo.trailer && (
                          <>
                            <span style={{ color: 'var(--text-muted)' }}> + </span>
                            <span style={{ color: 'var(--color-info)' }}>
                              {combo.trailer.trailer_number}
                            </span>
                            {combo.trailer.trailer_type && (
                              <span style={{ color: 'var(--text-muted)' }}> ({getTrailerTypeLabel(combo.trailer.trailer_type)})</span>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ marginTop: '0.25rem' }}>
                        <ComboCapacity truck={combo.truck} trailer={combo.trailer} capKg={combo.combined_max_weight_kg} units={units} />
                      </div>
                      {combo.default_driver && (
                        <div style={{ color: 'var(--color-success)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                          Default Driver: {combo.default_driver.name || combo.default_driver.email || 'Unknown'}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleEdit(combo)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--accent-blue)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Edit
                      </button>
                      {deleteConfirmId === combo.id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Sure?</span>
                          <button onClick={() => handleDelete(combo)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Yes</button>
                          <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirmId(combo.id)} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                      )}  
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--surface-2)',
              color: 'var(--text-muted)',
              fontSize: '0.9rem'
            }}>
              <p style={{ margin: 0 }}>
                {combinations.length} combination{combinations.length !== 1 ? 's' : ''} | 
                {trucks.length} truck{trucks.length !== 1 ? 's' : ''} | 
                {trailers.length} trailer{trailers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FleetCombinationsModal;
